if (!customElements.get('search-box')) {
  const normalise = (text) => text.trim().toLowerCase().replace(/\s+/g, ' ');
  const pathOf = (href) => new URL(href, window.location.origin).pathname;
  // Shopify adds tracking parameters to suggestion links; a recent search keeps the page itself.
  const destination = (href) => {
    const url = new URL(href, window.location.origin);
    const q = url.searchParams.get('q');
    return url.pathname + (q ? `?q=${encodeURIComponent(q)}` : '');
  };

  // Recent searches stay on the device for 90 days after the last search: the words, and the page
  // a chosen suggestion led to.
  const RECENT_KEY = 'robey_recent_searches';
  const RECENT_LIFETIME = 90 * 24 * 60 * 60 * 1000;
  // As many as the box shows, so removing one never brings back an older one.
  const RECENT_MAX = 3;
  // Suggestions while typing, and of those Shopify's search terms (with the theme setting on).
  const SUGGESTIONS = 4;
  const SEARCH_TERMS = 2;
  // Wait after the last key before searching.
  const PAUSE = 120;
  const recent = {
    read() {
      try {
        const data = JSON.parse(localStorage.getItem(RECENT_KEY));
        if (data && data.expires > Date.now()) return data.terms;
        localStorage.removeItem(RECENT_KEY);
      } catch (error) {
        // Storage unavailable or unreadable.
      }
      return [];
    },
    write(entries) {
      try {
        if (entries.length) {
          localStorage.setItem(RECENT_KEY, JSON.stringify({ terms: entries, expires: Date.now() + RECENT_LIFETIME }));
        } else {
          localStorage.removeItem(RECENT_KEY);
        }
      } catch (error) {
        // Storage unavailable.
      }
      document.querySelectorAll('search-box').forEach((box) => box.renderRecent && box.renderRecent());
    },
    add(term, url) {
      const clean = term.trim().slice(0, 60);
      if (!clean) return;
      const others = recent.read().filter((entry) => normalise(entry.term) !== normalise(clean));
      recent.write([{ term: clean, url: url || undefined }, ...others].slice(0, RECENT_MAX));
    },
    remove(term) {
      recent.write(recent.read().filter((entry) => entry.term !== term));
    },
  };

  class SearchBox extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.search-box__input');
      this.results = this.querySelector('.search-box__results');
      this.isLayer = this.classList.contains('search-box--layer');
      this.cache = new Map();
      // Bestseller titles per collection path, or a promise while loading.
      this.bestsellers = new Map();
      this.noResults = new Set();
      this.active = -1;
      // Menu "Go straight to a page": link name = search terms, the first one shown; link = destination.
      this.direct = new Map();
      this.querySelector('.search-box__direct')?.content.querySelectorAll('li').forEach((item) => {
        item.dataset.terms.split(',').forEach((term) => this.direct.set(normalise(term), item));
      });

      this.recentBox = this.querySelector('.search-box__recent');
      this.recentList = this.recentBox.querySelector('.search-box__recent-list');
      this.recentItem = this.recentBox.querySelector('template').content.querySelector('li');
      this.recentList.addEventListener('click', (evt) => {
        const remove = evt.target.closest('.search-box__recent-remove');
        if (remove) recent.remove(remove.closest('li').dataset.term);
      });
      this.querySelector('.search-box__panel').addEventListener('click', (evt) => {
        const link = evt.target.closest('a');
        if (!link) return;
        this.rememberChoice(link);
        this.publishChoice(link);
      });
      this.renderRecent();

      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (evt) => this.onKeydown(evt));
      this.querySelector('.search-box__form').addEventListener('submit', (evt) => this.onSubmit(evt));
      this.querySelector('.search-box__clear').addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        if (this.isLayer) {
          this.input.focus();
        } else {
          this.close();
        }
      });

      if (this.isLayer) {
        this.querySelector('.search-box__cancel').addEventListener('click', () => this.close());
        // Dragging the results lowers the keyboard.
        const panel = this.querySelector('.search-box__panel');
        panel.addEventListener('touchmove', () => this.input.blur(), { passive: true });
        // Without a touch listener iOS does not show :active, the tap feedback.
        panel.addEventListener('touchstart', () => {}, { passive: true });
      } else {
        this.onPointerDown = (evt) => {
          if (!this.contains(evt.target)) this.close();
        };
        this.onResize = () => this.place();
      }
    }

    disconnectedCallback() {
      this.close();
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.renderRecent();
      this.classList.add('is-open');
      document.body.classList.add(this.isLayer ? 'search-layer-open' : 'search-open');
      if (!this.isLayer) {
        this.place();
        document.addEventListener('pointerdown', this.onPointerDown);
        window.addEventListener('resize', this.onResize);
      }
    }

    // The open field sits in the middle of the page; the panel hangs from the bottom of the header bar.
    place() {
      const bar = this.getBoundingClientRect();
      const header = this.closest('.pageheader') || this;
      const width = parseFloat(getComputedStyle(this).getPropertyValue('--search-box-open-width'));
      const middle = document.documentElement.clientWidth / 2;
      this.style.setProperty('--search-box-shift', `${Math.round(bar.right - middle - width / 2)}px`);
      this.style.setProperty('--search-box-drop', `${Math.round(header.getBoundingClientRect().bottom - bar.bottom)}px`);
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove(this.isLayer ? 'search-layer-open' : 'search-open');
      if (!this.isLayer) {
        document.removeEventListener('pointerdown', this.onPointerDown);
        window.removeEventListener('resize', this.onResize);
      }
      this.setActive(-1);
      this.input.blur();
    }

    // Terms that returned nothing are not kept; unknown ones (Enter before the results came in) are.
    remember(term) {
      if (!this.noResults.has(normalise(term))) recent.add(term);
    }

    // A suggestion or recent search keeps its own words and page; all results keeps the typed
    // words. Products and popular searches stay out.
    rememberChoice(link) {
      if (link.classList.contains('search-box__recent-link')) {
        const item = link.closest('li');
        recent.add(item.dataset.term, item.dataset.url);
      } else if (link.closest('.search-box__suggestions') && !link.classList.contains('search-box__view-all')) {
        recent.add(link.querySelector('.search-box__label').textContent, destination(link.href));
      } else if (!link.closest('.search-box__popular') && !link.classList.contains('search-box__product')) {
        this.remember(this.input.value);
      }
    }

    renderRecent() {
      const entries = recent.read();
      this.recentList.replaceChildren(
        ...entries.map(({ term, url }) => {
          const item = this.recentItem.cloneNode(true);
          const link = item.querySelector('a');
          const search = new URL(link.getAttribute('href'), window.location.origin);
          search.searchParams.set('q', term);
          item.dataset.term = term;
          if (url) item.dataset.url = url;
          link.href = url || this.directHref(term) || search.pathname + search.search;
          item.querySelector('.search-box__label').textContent = term;
          return item;
        })
      );
      this.recentBox.hidden = entries.length === 0;
      this.classList.toggle('has-recent', entries.length > 0);
    }

    onInput() {
      const terms = this.input.value.trim();
      this.classList.toggle('has-terms', terms.length > 0);
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.setActive(-1);
      this.querySelector('.search-box__nothing').hidden = true;
      // From two characters; with fewer the start content stays.
      if (terms.length < 2) {
        this.setLoading(false);
        this.results.replaceChildren();
        this.classList.remove('has-results');
        return;
      }
      if (this.cache.has(terms)) {
        this.show(this.cache.get(terms), terms);
        return;
      }
      this.setLoading(true);
      this.timer = setTimeout(() => this.fetchResults(terms), PAUSE);
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
    }

    async fetchResults(terms) {
      // The bestsellers load alongside the results.
      const collection = this.rankedCollection(terms);
      if (collection) this.bestsellersOf(collection);
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('q', terms);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', this.dataset.types);
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', this.dataset.fields);

      this.controller = new AbortController();
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) {
          this.setLoading(false);
          return;
        }
        const html = await response.text();
        this.cache.set(terms, html);
        if (this.input.value.trim() === terms) this.show(html, terms);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setLoading(false);
        throw error;
      }
    }

    show(html, terms) {
      const found = new DOMParser().parseFromString(html, 'text/html').querySelector('.search-box__found');
      if (!found) {
        this.setLoading(false);
        return;
      }
      // Menu destinations show also when Shopify did not suggest them: an exact term first, a term
      // the typed words complete after Shopify's collections.
      const suggestions = found.querySelector('.search-box__suggestions');
      const direct = this.directMatches(terms);
      const none = found.querySelector('.search-box__none');
      if (none && direct.length) none.remove();
      if (found.querySelector('.search-box__none')) {
        this.noResults.add(normalise(terms));
        // Nothing found: the start content stays, under a short note.
        const nothing = this.querySelector('.search-box__nothing');
        nothing.textContent = found.querySelector('.search-box__none').textContent.trim();
        nothing.hidden = false;
        this.setLoading(false);
        this.results.replaceChildren();
        this.classList.remove('has-results');
        return;
      }
      this.noResults.delete(normalise(terms));
      // "View all results" stays the last line.
      const viewAll = suggestions.querySelector('.search-box__view-all').closest('li');
      viewAll.remove();
      const shown = (item) => [...suggestions.children].find((li) => pathOf(li.firstElementChild.href) === pathOf(item.querySelector('a').href));
      let after = [...suggestions.children].filter((li) => !li.querySelector('.search-box__query')).pop();
      direct.filter((hit) => !hit.exact).forEach(({ item }) => {
        if (shown(item)) return;
        const clone = item.cloneNode(true);
        if (after) after.after(clone);
        else suggestions.prepend(clone);
        after = clone;
      });
      direct.filter((hit) => hit.exact).reverse().forEach(({ item }) => suggestions.prepend(shown(item) || item.cloneNode(true)));
      // Two of Shopify's search terms, last. A term is skipped when it is the typed words or a line
      // above it, or differs from one only by a plural ("baselayer" under "baselayers").
      const alike = (line, term) => line.startsWith(term) || (term.startsWith(line) && term.length - line.length <= 2);
      const queries = [...suggestions.querySelectorAll('.search-box__query')].map((link) => link.closest('li'));
      const pages = [...suggestions.children].filter((li) => !queries.includes(li));
      const labels = [normalise(terms), ...pages.map((li) => normalise(li.textContent))];
      const kept = [];
      queries.forEach((li) => {
        const term = normalise(li.textContent);
        if (kept.length < SEARCH_TERMS && !labels.some((label) => alike(label, term))) {
          kept.push(li);
          labels.push(term);
        } else {
          li.remove();
        }
      });
      suggestions.append(...kept, viewAll);
      pages.slice(SUGGESTIONS - kept.length).forEach((item) => item.remove());
      suggestions.hidden = false;
      found.querySelector('.search-box__suggestions-heading').hidden = suggestions.children.length === 1;
      // Collection titles get the same emphasis as Shopify's query suggestions: typed part regular.
      suggestions.querySelectorAll('[data-title]').forEach((label) => {
        const title = label.dataset.title;
        const start = title.toLowerCase().indexOf(terms.toLowerCase());
        if (start < 0 || (start > 0 && /\w/.test(title[start - 1]))) return;
        const mark = document.createElement('mark');
        mark.textContent = title.slice(start, start + terms.length);
        const parts = [title.slice(0, start), mark, title.slice(start + terms.length)].map((part) => {
          if (typeof part !== 'string') return part;
          const span = document.createElement('span');
          span.textContent = part;
          return span;
        });
        label.replaceChildren(...parts.filter((part) => part.textContent));
      });
      // Shopify orders products by text; for a collection from the menu they follow its
      // bestsellers, others come after.
      const products = found.querySelector('.search-box__products');
      const collection = products?.children.length > 1 && this.rankedCollection(terms);
      const ranking = collection && this.bestsellersOf(collection);
      if (collection && !Array.isArray(ranking)) {
        ranking.then(() => {
          if (this.input.value.trim() === terms) this.show(html, terms);
        });
        return;
      }
      if (ranking?.length) {
        const rank = (item) => {
          const index = ranking.indexOf(normalise(item.querySelector('.search-box__title').textContent));
          return index < 0 ? ranking.length : index;
        };
        products.append(...[...products.children].sort((a, b) => rank(a) - rank(b)));
      }
      if (products) [...products.children].slice(Number(this.dataset.products)).forEach((item) => item.remove());
      // Shopify links some products, such as bundles, to a variant, which would preselect its
      // sizes; they open without one, as from a collection.
      products?.querySelectorAll('.search-box__product').forEach((link) => {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.delete('variant');
        link.setAttribute('href', url.pathname + url.search);
      });
      this.setLoading(false);
      this.results.replaceChildren(found);
      this.classList.add('has-results');
      this.setActive(-1);
    }

    // With the theme setting on: the collection in the menu "Go straight to a page" of the term
    // equal to the typed words, or the one collection of all terms they start ("korte broe" for
    // Shorts). "sokken zwart" and words outside the menu keep Shopify's order.
    rankedCollection(terms) {
      if (!('bestsellers' in this.dataset)) return null;
      const typed = normalise(terms);
      const hrefs = this.direct.has(typed)
        ? [this.directHref(terms)]
        : [...this.direct].filter(([term]) => term.startsWith(typed)).map(([, item]) => item.querySelector('a').getAttribute('href'));
      const paths = new Set(hrefs.map(pathOf));
      const [path] = paths;
      return paths.size === 1 && /\/collections\/[^/]+$/.test(path) ? path : null;
    }

    // A collection's bestseller titles, loaded once per page view; a promise while loading.
    bestsellersOf(path) {
      if (!this.bestsellers.has(path)) this.bestsellers.set(path, this.loadBestsellers(path));
      return this.bestsellers.get(path);
    }

    async loadBestsellers(path) {
      const url = new URL(path, window.location.origin);
      url.searchParams.set('section_id', 'search-box-bestsellers');
      url.searchParams.set('sort_by', 'best-selling');
      let titles = [];
      try {
        const response = await fetch(url);
        if (response.ok) {
          const html = await response.text();
          const data = new DOMParser().parseFromString(html, 'text/html').querySelector('.search-box__bestsellers');
          titles = JSON.parse(data.textContent).map(normalise);
        }
      } catch (error) {
        // Shopify's order stays.
      }
      this.bestsellers.set(path, titles);
    }

    // A full article code with its colour ("rs5005-100") opens that product, when it is among the results.
    codeHref(terms) {
      const code = normalise(terms);
      if (!/^[a-z]+\d+-\d+$/.test(code)) return null;
      const product = [...this.results.querySelectorAll('.search-box__product')].find((link) => pathOf(link.href).endsWith(`-${code}`));
      return product?.getAttribute('href');
    }

    directHref(terms) {
      return this.direct.get(normalise(terms))?.querySelector('a').getAttribute('href');
    }

    // Menu links in menu order. A term equal to the typed words or that the typed words start with
    // ("trui zwart" matches "trui") is exact; a term starting with the typed words completes them,
    // from 2 letters, where the search starts, so all suggestions come at once.
    directMatches(terms) {
      const typed = normalise(terms);
      const hits = new Map();
      this.direct.forEach((item, term) => {
        const exact = term === typed || typed.startsWith(`${term} `);
        if (!exact && !(typed.length >= 2 && term.startsWith(typed))) return;
        const hit = hits.get(item) || { item, exact: false };
        hit.exact = hit.exact || exact;
        hits.set(item, hit);
      });
      return [...hits.values()];
    }

    options() {
      const scope = this.classList.contains('has-results') ? this.results : this.querySelector('.search-box__start');
      return [...scope.querySelectorAll('a')].filter((link) => link.offsetParent);
    }

    setActive(index) {
      const options = this.options();
      options.forEach((option, i) => option.classList.toggle('is-active', i === index));
      this.active = index;
    }

    onKeydown(evt) {
      if (evt.key === 'Escape') {
        this.close();
        return;
      }
      if (evt.key !== 'ArrowDown' && evt.key !== 'ArrowUp') return;
      const count = this.options().length;
      if (!count) return;
      evt.preventDefault();
      const step = evt.key === 'ArrowDown' ? 1 : -1;
      this.setActive((this.active + step + count) % count);
    }

    onSubmit(evt) {
      const option = this.options()[this.active];
      if (option) {
        this.rememberChoice(option);
        this.publishChoice(option);
      } else {
        this.remember(this.input.value);
      }
      const code = option ? null : this.codeHref(this.input.value);
      const href = option ? option.href : this.directHref(this.input.value) || code;
      if (!href) return;
      if (!option) this.publishChoice(null, href, code ? 'code' : 'menu');
      evt.preventDefault();
      window.location.href = href;
    }

    // Shopify records a search (search_submitted) on the search results page only. Leaving the box
    // for any other page sends this event instead, for a pixel to pass on.
    publishChoice(link, href = link.href, kind = 'menu') {
      const searchPage = pathOf(this.querySelector('.search-box__form').action);
      if (pathOf(href) === searchPage) return;
      if (link?.classList.contains('search-box__recent-link')) kind = 'recent';
      else if (link?.closest('.search-box__popular')) kind = 'popular';
      else if (link?.closest('.search-box__suggestions')) kind = 'suggestion';
      else if (link) kind = 'product';
      const searchTerm = ['recent', 'popular'].includes(kind) ? link.textContent.trim() : this.input.value.trim();
      if (!searchTerm) return;
      window.Shopify?.analytics?.publish?.('search_box_selected', { searchTerm, destination: destination(href), kind });
    }
  }

  customElements.define('search-box', SearchBox);

  // Opening and focusing in the same tap lets iOS show the keyboard straight away.
  document.addEventListener('click', (evt) => {
    const toggle = evt.target.closest('[data-search-open]');
    if (!toggle) return;
    const layer = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!layer) return;
    evt.preventDefault();
    layer.open();
    layer.input.focus();
  });
}
